import { get, post, put, del } from "@/shared/utils/fetchApi";

/**
 * Interface para as configurações de Workflow Base
 */
export interface WorkflowBase {
  id: string;
  name: string;
  description: string;
  status: "draft" | "active" | "archived";
  version: number;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
  agent_base_id: string | null;
  product_id: string | null;
  settings: Record<string, unknown> | null;
}

/**
 * Interface para criação de Workflow Base
 */
export interface CreateWorkflowBaseDTO {
  name: string;
  description?: string;
  status?: string;
  version?: number;
  created_by?: string;
  agent_id?: string;
  agent_base_id?: string;
  product_id?: string;
  settings?: Record<string, unknown>;
}

/**
 * Interface para atualização de Workflow Base
 */
export interface UpdateWorkflowBaseDTO {
  name?: string;
  description?: string;
  status?: string;
  version?: number;
  updated_by?: string;
  agent_base_id?: string;
  settings?: Record<string, unknown>;
}

/**
 * Classe de serviço para encapsular todas as operações relacionadas aos workflow bases
 * Implementa o padrão Facade para simplificar as chamadas à API
 */
export class WorkflowBaseService {
  private static readonly BASE_URL = "/workflow-bases";

  /**
   * Obtém todos os workflow bases
   * @returns Lista de workflow bases
   */
  static async getAll(): Promise<WorkflowBase[]> {
    try {
      return await get<WorkflowBase[]>(this.BASE_URL);
    } catch (error) {
      console.error("Erro ao buscar workflow bases:", error);
      throw error;
    }
  }

  /**
   * Obtém workflow base pelo ID
   * @param id ID do workflow base
   * @returns Dados do workflow base
   */
  static async getById(id: string): Promise<WorkflowBase> {
    try {
      return await get<WorkflowBase>(`${this.BASE_URL}/${id}`);
    } catch (error) {
      console.error(`Erro ao buscar workflow base com ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Obtém workflow bases pelo ID do agent base
   * @param agentBaseId ID do agent base
   * @returns Lista de workflow bases
   */
  static async getByAgentBaseId(agentBaseId: string): Promise<WorkflowBase[]> {
    try {
      return await get<WorkflowBase[]>(
        `${this.BASE_URL}/by-agent-base/${agentBaseId}`,
      );
    } catch (error) {
      console.error(
        `Erro ao buscar workflow bases para agent base ${agentBaseId}:`,
        error,
      );
      throw error;
    }
  }

  /**
   * Obtém workflow bases por status
   * @param status Status do workflow base
   * @returns Lista de workflow bases
   */
  static async getByStatus(status: string): Promise<WorkflowBase[]> {
    try {
      return await get<WorkflowBase[]>(
        `${this.BASE_URL}?status=${encodeURIComponent(status)}`,
      );
    } catch (error) {
      console.error(
        `Erro ao buscar workflow bases com status ${status}:`,
        error,
      );
      throw error;
    }
  }

  /**
   * Cria um novo workflow base
   * @param data Dados do workflow base a ser criado
   * @returns Workflow base criado
   */
  static async create(data: CreateWorkflowBaseDTO): Promise<WorkflowBase> {
    try {
      return await post<WorkflowBase>(this.BASE_URL, data);
    } catch (error) {
      console.error("Erro ao criar workflow base:", error);
      throw error;
    }
  }

  /**
   * Atualiza um workflow base existente pelo ID
   * @param id ID do workflow base
   * @param data Dados a serem atualizados
   * @returns Workflow base atualizado
   */
  static async update(
    id: string,
    data: UpdateWorkflowBaseDTO,
  ): Promise<WorkflowBase> {
    try {
      return await put<WorkflowBase>(`${this.BASE_URL}/${id}`, data);
    } catch (error) {
      console.error(`Erro ao atualizar workflow base com ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Remove um workflow base existente pelo ID
   * v2: 204 No Content (returns void). Legacy: 200 {success, message}.
   */
  static async delete(id: string): Promise<void> {
    try {
      await del<void>(`${this.BASE_URL}/${id}`);
      return;
    } catch (error) {
      console.error(`Erro ao remover workflow base com ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Obtém um nó pelo ID
   * @param nodeId ID do nó
   * @returns Dados do nó
   */
  static async getNode(
    nodeId: string,
  ): Promise<{ data: Record<string, unknown> | null; error: Error | null }> {
    try {
      const node = await get<Record<string, unknown>>(`/nodes/${nodeId}`);
      return { data: node ?? null, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error(String(error)),
      };
    }
  }

  /**
   * Obtém um workflow pelo ID
   * @param workflowId ID do workflow
   * @returns Dados do workflow
   */
  static async getWorkflow(
    workflowId: string,
  ): Promise<{ data: Record<string, unknown> | null; error: Error | null }> {
    try {
      const row = await get<Record<string, unknown>>(
        `${this.BASE_URL}/${workflowId}`,
      );
      return { data: row ?? null, error: null };
    } catch (error) {
      console.error(`[WorkflowBaseService] Erro ao buscar workflow:`, error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error(String(error)),
      };
    }
  }

  /**
   * Atualiza um workflow existente
   * @param workflow Dados do workflow a ser atualizado
   * @param organizationId ID da organização (opcional, usa o valor existente ou padrão se não fornecido)
   * @param workspaceId ID do produto (opcional, usa o valor existente ou padrão se não fornecido)
   * @returns Status da atualização
   */
  static async updateWorkflow(
    workflow: Record<string, unknown>,
    organizationId?: string,
    workspaceId?: string,
  ): Promise<{ error: Error | null; data?: Record<string, unknown> }> {
    try {
      // Extrair os dados principais do workflow e os settings
      let workflowData: Record<string, unknown> = {};
      let workflowSettings: Record<string, unknown> = {};

      // Verificar diferentes estruturas de resposta possíveis
      if (workflow.data) {
        // Nova estrutura: { data: { ... } }
        workflowData = workflow.data as Record<string, unknown>;
        workflowSettings =
          (workflowData.settings as Record<string, unknown>) || {};
      } else {
        // Estrutura direta: { id: '...', settings: { ... } }
        workflowData = workflow;
        workflowSettings = (workflow.settings as Record<string, unknown>) || {};
      }

      // Garantir que temos o ID do workflow
      if (!workflowData.id) {
        console.error(
          "[WorkflowBaseService] ID do workflow não encontrado para atualização",
        );
        return { error: new Error("ID do workflow não encontrado") };
      }

      // Garantir que settings exista
      if (!workflowSettings) {
        workflowSettings = {};
      }

      // Usar os IDs fornecidos ou manter os existentes, sem fallback hard-coded
      workflowSettings.organizationId =
        organizationId || workflowSettings.organizationId;
      workflowSettings.workspaceId =
        workspaceId || workflowSettings.workspaceId;

      // Validar que temos os IDs necessários
      if (!workflowSettings.organizationId) {
        console.error("[WorkflowBaseService] ID da organização não encontrado");
        return {
          error: new Error("ID da organização não encontrado ou inválido"),
        };
      }

      if (!workflowSettings.workspaceId) {
        console.error("[WorkflowBaseService] ID do produto não encontrado");
        return { error: new Error("ID do produto não encontrado ou inválido") };
      }

      // Criar o objeto de atualização com a estrutura esperada pelo backend
      const updateData = {
        id: workflowData.id,
        name: workflowData.name || "Workflow sem nome",
        description: workflowData.description || "",
        status: workflowData.status || "active",
        settings: workflowSettings,
      };

      try {
        const response = await put<Record<string, unknown>>(
          `${this.BASE_URL}/${updateData.id}`,
          updateData,
        );
        return { error: null, data: response };
      } catch (updateError) {
        return {
          error:
            updateError instanceof Error
              ? updateError
              : new Error(String(updateError)),
        };
      }
    } catch (error) {
      console.error(`[WorkflowBaseService] Erro ao atualizar workflow:`, error);
      return {
        error: error instanceof Error ? error : new Error(String(error)),
      };
    }
  }

  /**
   * Obtém todos os nós disponíveis
   * @returns Lista de nós
   */
  static async getAllNodes(): Promise<{
    data: Record<string, unknown>[];
    error: Error | null;
  }> {
    try {
      const result = await get<{
        data: Record<string, unknown>[];
        pagination: unknown;
      }>("/nodes");
      return { data: result.data, error: null };
    } catch (error) {
      return {
        data: [],
        error: error instanceof Error ? error : new Error(String(error)),
      };
    }
  }

  /**
   * Obtém um agent_base pelo ID
   * @param id ID do agent_base
   * @returns Dados do agent_base
   */
  static async getAgentBaseById(id: string): Promise<Record<string, unknown>> {
    try {
      // Importação dinâmica para evitar dependência circular
      const { AgentBaseService } =
        await import("@/features/Agents/services/agentBaseService");
      return (await AgentBaseService.getById(id)) as unknown as Record<
        string,
        unknown
      >;
    } catch (error) {
      console.error(
        `[WorkflowBaseService] Erro ao buscar agent_base com ID ${id}:`,
        error,
      );
      throw error;
    }
  }
}
